import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Sparkles, Loader2, Mic, Check, Star, Trash2, Plus, ListChecks, MessagesSquare,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/fetch-with-auth";

export const Route = createFileRoute("/_authenticated/portal/interview")({
  component: InterviewCoachPage,
});

type Q = {
  category: "behavioural" | "technical" | "situational" | "values" | "curveball";
  question: string;
  why_asked: string;
  coach_tip: string;
  model_answer_outline: string;
};
type Plan = {
  role: string;
  seniority: string;
  overview: string;
  competencies: string[];
  questions: Q[];
  mock_script: { speaker: "INTERVIEWER" | "CANDIDATE" | "STAGE"; line: string }[];
  closing_questions_to_ask: string[];
  red_flags_to_avoid: string[];
};

type Progress = {
  // key = `${role}::${index}` -> { rating: 0-5, notes: string, doneAt: string }
  [key: string]: { rating: number; notes: string; doneAt: string };
};

const STORAGE_KEY = "ndl_interview_progress_v1";
const PLAN_KEY = "ndl_interview_plan_v1";

function loadProgress(): Progress {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveProgress(p: Progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

function InterviewCoachPage() {
  const [targetRole, setTargetRole] = useState("");
  const [seniority, setSeniority] = useState("mid");
  const [focus, setFocus] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [count, setCount] = useState(10);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [progress, setProgress] = useState<Progress>({});
  const [tab, setTab] = useState<"questions" | "mock" | "progress">("questions");

  useEffect(() => {
    setProgress(loadProgress());
    try {
      const raw = localStorage.getItem(PLAN_KEY);
      if (raw) setPlan(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    if (plan) localStorage.setItem(PLAN_KEY, JSON.stringify(plan));
  }, [plan]);

  const gen = useMutation({
    mutationFn: async () => {
      const res = await fetchWithAuth("/api/interview-coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetRole, seniority, jobDescription, focus, count }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate");
      return data.plan as Plan;
    },
    onSuccess: (p) => setPlan(p),
  });

  function updateProgress(key: string, patch: Partial<Progress[string]>) {
    setProgress((prev) => {
      const base = prev[key] ?? { rating: 0, notes: "", doneAt: new Date().toISOString() };
      const next = { ...prev, [key]: { ...base, ...patch } };
      saveProgress(next);
      return next;
    });
  }
  function clearProgress() {
    if (!confirm("Clear all interview progress?")) return;
    saveProgress({});
    setProgress({});
  }

  const stats = useMemo(() => {
    if (!plan) return { done: 0, total: 0, avg: 0 };
    const keys = plan.questions.map((_, i) => `${plan.role}::${i}`);
    const entries = keys.map((k) => progress[k]).filter(Boolean);
    const done = entries.length;
    const avg = entries.length
      ? entries.reduce((s, e) => s + (e.rating || 0), 0) / entries.length
      : 0;
    return { done, total: plan.questions.length, avg: Math.round(avg * 10) / 10 };
  }, [plan, progress]);

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-navy">AI Interview Coach</h2>
        <p className="mt-1 text-sm text-foreground/70">
          Generate role-specific practice questions, a realistic mock interview script,
          and track your progress. Want a live human mock with feedback?{" "}
          <Link to="/book" className="text-navy underline">Book a coaching session</Link>.
        </p>
      </header>

      <section className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h3 className="text-sm font-medium text-navy">1. Tell us about the role</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">Target role *</span>
            <input
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="e.g. Compliance Officer, HR Manager, Paralegal"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">Seniority</span>
            <select
              value={seniority}
              onChange={(e) => setSeniority(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="entry">Entry-level / graduate</option>
              <option value="mid">Mid-level</option>
              <option value="senior">Senior</option>
              <option value="executive">Executive / management</option>
            </select>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">Focus areas (optional)</span>
            <input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="e.g. POPIA, conflict handling, stakeholder management"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">Number of questions</span>
            <input
              type="number" min={5} max={20}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-foreground/80">
            Paste the job advert / description (optional but recommended)
          </span>
          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            rows={5}
            placeholder="Paste the job advert here for a sharper, JD-aligned question set."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => gen.mutate()}
            disabled={gen.isPending || !targetRole}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {plan ? "Regenerate plan" : "Generate practice plan"}
          </button>
          {gen.error && <p className="text-xs text-red-600">{(gen.error as Error).message}</p>}
        </div>
      </section>

      {plan && (
        <>
          <section className="rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {plan.seniority} · {plan.role}
                </p>
                <p className="mt-1 text-sm text-foreground/80">{plan.overview}</p>
                {plan.competencies?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {plan.competencies.map((c, i) => (
                      <span key={i} className="rounded bg-muted px-2 py-0.5 text-[11px] text-foreground/70">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right text-xs">
                <p className="text-muted-foreground">Progress</p>
                <p className="font-semibold text-navy">
                  {stats.done}/{stats.total} done · avg {stats.avg}/5
                </p>
              </div>
            </div>
          </section>

          <div className="flex gap-1 border-b border-border text-sm">
            {([
              ["questions", "Practice questions", ListChecks],
              ["mock", "Mock interview", MessagesSquare],
              ["progress", "Progress tracker", Star],
            ] as const).map(([k, label, Icon]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 ${
                  tab === k ? "border-navy text-navy" : "border-transparent text-foreground/60 hover:text-navy"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>

          {tab === "questions" && (
            <ul className="space-y-3">
              {plan.questions.map((q, i) => {
                const key = `${plan.role}::${i}`;
                const p = progress[key];
                return (
                  <li key={i} className="rounded-lg border border-border bg-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="inline-block rounded bg-navy/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-navy">
                          {q.category}
                        </span>
                        <p className="mt-2 font-medium text-navy">{i + 1}. {q.question}</p>
                      </div>
                      <button
                        onClick={() => updateProgress(key, { doneAt: new Date().toISOString() })}
                        className={`shrink-0 rounded-md border px-2 py-1 text-xs ${
                          p ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-input hover:border-navy"
                        }`}
                      >
                        <Check className="inline h-3 w-3" /> {p ? "Practised" : "Mark practised"}
                      </button>
                    </div>
                    <details className="mt-2 text-sm">
                      <summary className="cursor-pointer text-xs text-navy hover:underline">
                        Coach tip & model outline
                      </summary>
                      <div className="mt-2 space-y-2 text-sm text-foreground/80">
                        <p><strong className="text-navy">Why asked:</strong> {q.why_asked}</p>
                        <p><strong className="text-navy">Coach tip:</strong> {q.coach_tip}</p>
                        <p className="whitespace-pre-wrap">
                          <strong className="text-navy">STAR outline:</strong> {q.model_answer_outline}
                        </p>
                      </div>
                    </details>
                    <div className="mt-3 grid gap-2 sm:grid-cols-[160px_1fr]">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Self-rating</p>
                        <div className="mt-1 flex gap-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              onClick={() => updateProgress(key, { rating: n })}
                              className={`rounded p-0.5 ${
                                (p?.rating ?? 0) >= n ? "text-amber-500" : "text-muted-foreground/40 hover:text-amber-400"
                              }`}
                              aria-label={`Rate ${n} of 5`}
                            >
                              <Star className="h-4 w-4 fill-current" />
                            </button>
                          ))}
                        </div>
                      </div>
                      <label className="block">
                        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                          My notes / draft answer
                        </span>
                        <textarea
                          rows={2}
                          value={p?.notes ?? ""}
                          onChange={(e) => updateProgress(key, { notes: e.target.value })}
                          placeholder="Write your STAR answer here. Saved to this device."
                          className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:border-navy focus:outline-none"
                        />
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {tab === "mock" && (
            <section className="rounded-lg border border-border bg-card p-5">
              <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Mic className="h-4 w-4 text-navy" />
                Read it aloud, or take turns with a friend. Stage directions are in italics.
              </div>
              <ol className="space-y-2 text-sm">
                {plan.mock_script.map((line, i) => (
                  <li key={i} className="grid grid-cols-[110px_1fr] gap-3">
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-widest ${
                        line.speaker === "INTERVIEWER"
                          ? "text-navy"
                          : line.speaker === "CANDIDATE"
                            ? "text-gold"
                            : "italic text-muted-foreground"
                      }`}
                    >
                      {line.speaker}
                    </span>
                    <span
                      className={
                        line.speaker === "STAGE" ? "italic text-muted-foreground" : "text-foreground/85"
                      }
                    >
                      {line.line}
                    </span>
                  </li>
                ))}
              </ol>
              {plan.closing_questions_to_ask?.length > 0 && (
                <div className="mt-5 rounded-md bg-muted/40 p-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-navy">
                    Strong questions to ask the panel
                  </p>
                  <ul className="mt-2 list-inside list-disc text-sm text-foreground/80">
                    {plan.closing_questions_to_ask.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
              {plan.red_flags_to_avoid?.length > 0 && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">Red flags to avoid</p>
                  <ul className="mt-2 list-inside list-disc text-sm text-amber-900/80">
                    {plan.red_flags_to_avoid.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
            </section>
          )}

          {tab === "progress" && (
            <section className="rounded-lg border border-border bg-card p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-3xl font-display text-navy">{stats.done}/{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Questions practised</p>
                </div>
                <div>
                  <p className="text-3xl font-display text-gold">{stats.avg}/5</p>
                  <p className="text-xs text-muted-foreground">Average self-rating</p>
                </div>
                <button
                  onClick={clearProgress}
                  className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs hover:border-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" /> Reset progress
                </button>
              </div>
              <div className="mt-4 h-2 w-full rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-navy transition-all"
                  style={{ width: `${stats.total ? (stats.done / stats.total) * 100 : 0}%` }}
                />
              </div>
              <ul className="mt-5 space-y-2 text-sm">
                {plan.questions.map((q, i) => {
                  const p = progress[`${plan.role}::${i}`];
                  return (
                    <li key={i} className="flex items-start justify-between gap-3 border-b border-border/50 pb-2">
                      <span className="text-foreground/80">{i + 1}. {q.question}</span>
                      <span className="shrink-0 text-xs">
                        {p ? (
                          <span className="text-emerald-700">
                            ★ {p.rating || 0}/5 · {new Date(p.doneAt).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Not started</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}

      {!plan && !gen.isPending && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          <Plus className="mx-auto mb-2 h-5 w-5" />
          Your practice plan, mock script, and tracker will appear here once generated.
        </div>
      )}
    </div>
  );
}
